import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, MenuItem, MenuList, Popover, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TimelineLaneEnum } from "@/domain/troubleshooting.enum";
import { TimelineEvent } from "@/domain/troubleshooting.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import {
  buildAxisTicks,
  clusterLaneEvents,
  DEFAULT_TRACK_WIDTH_PX,
  isEventFocused,
  positionPercent,
  TimelineWindow,
  TimelineZoom,
} from "@/utils/troubleshooting.utils";
import { OutcomeIcon } from "./outcome";
import { getEventTypeMeta } from "./eventType";
import {
  ClusterMarker,
  SingleMarker,
  TIMELINE_CLOCK_FORMAT,
  TIMELINE_TIME_FORMAT,
} from "./TimelineMarker";

export { TIMELINE_TIME_FORMAT };

const LABEL_WIDTH = 170;
const LANE_HEIGHT = 38;

interface TimelineSwimlaneProps {
  events: TimelineEvent[];
  window: TimelineWindow;
  zoom: TimelineZoom;
  lanes: TimelineLaneEnum[];
  selectedEventId?: string;
  focusGeofenceId?: string;
  onSelect: (event: TimelineEvent) => void;
  /** Live mode: draw the "now" line and keep it in view while following. */
  live?: boolean;
  /** Events that arrived with the last Live refresh (highlighted). */
  newEventIds?: ReadonlySet<string>;
}

export interface TimelineSwimlaneHandle {
  /**
   * Scroll the most recent of `among` (e.g. the events of the last Live
   * refresh) — or of all events when empty — into view, and resume
   * following "now".
   */
  scrollToLatest: (among?: ReadonlySet<string>) => void;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

/** The marker element (single or cluster) that shows `eventId`. */
const findMarker = (
  container: HTMLElement | null,
  eventId: string,
): HTMLElement | undefined =>
  Array.from(
    container?.querySelectorAll<HTMLElement>("[data-event-ids]") || [],
  ).find((el) => (el.dataset.eventIds || "").split(" ").includes(eventId));

const TimelineSwimlane = forwardRef<
  TimelineSwimlaneHandle,
  TimelineSwimlaneProps
>(function TimelineSwimlane(
  {
    events,
    window,
    zoom,
    lanes,
    selectedEventId,
    focusGeofenceId,
    onSelect,
    live = false,
    newEventIds = EMPTY_IDS,
  },
  ref,
) {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(DEFAULT_TRACK_WIDTH_PX);
  const [openCluster, setOpenCluster] = useState<{
    key: string;
    anchor: HTMLElement;
  } | null>(null);
  // Live: follow "now" until the user scrolls the swimlane themselves.
  const following = useRef(true);
  // Where our own last scroll put the container (to tell it from the user's).
  const expectedScrollLeft = useRef<number | null>(null);
  const scrolledToSelection = useRef("");
  const nowPct = live
    ? positionPercent(new Date().toISOString(), window.fromMs, window.toMs)
    : null;

  // Cluster by pixels: measure the real track (it can be wider than the
  // viewport when the swimlane scrolls).
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = (width: number) => {
      if (width > 0) setTrackWidth(width);
    };
    measure(track.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) =>
      measure(entries[0]?.contentRect.width ?? 0),
    );
    observer.observe(track);
    return () => observer.disconnect();
  }, []);

  const scrollContainerToNow = () => {
    const container = containerRef.current;
    if (!container || nowPct === null) return;
    // Nothing to do when the whole day already fits.
    if (container.scrollWidth <= container.clientWidth) return;
    const track = container.scrollWidth - LABEL_WIDTH - 32;
    const left = Math.max(
      0,
      LABEL_WIDTH + (nowPct / 100) * track - container.clientWidth * 0.7,
    );
    if (Math.abs(container.scrollLeft - left) < 1) return;
    expectedScrollLeft.current = left;
    container.scrollTo?.({ left });
  };

  useImperativeHandle(ref, () => ({
    scrollToLatest: (among) => {
      const candidates =
        among && among.size > 0
          ? events.filter((e) => among.has(e.id))
          : events;
      const latest = candidates.reduce<TimelineEvent | null>(
        (acc, e) =>
          !acc || new Date(e.occurredAt) > new Date(acc.occurredAt) ? e : acc,
        null,
      );
      if (!latest) return;
      following.current = true;
      findMarker(containerRef.current, latest.id)?.scrollIntoView?.({
        block: "center",
        inline: "center",
      });
    },
  }));

  // Live: on start and on each refresh, keep "now" in view — only inside the
  // swimlane and only while following, so the page itself never jumps.
  useEffect(() => {
    if (live && following.current) scrollContainerToNow();
  }, [live, newEventIds]);

  const ticks = useMemo(() => buildAxisTicks(window, zoom), [window, zoom]);

  const laneMarkers = useMemo(
    () =>
      lanes.map((lane) => ({
        lane,
        markers: clusterLaneEvents(
          events.filter((e) => e.lane === lane),
          window.fromMs,
          window.toMs,
          trackWidth,
        ),
      })),
    [events, lanes, window.fromMs, window.toMs, trackWidth],
  );

  // Bring the selected (or linked) marker into view once per selection, so a
  // Live refresh never moves the view again.
  useEffect(() => {
    if (!selectedEventId) {
      scrolledToSelection.current = "";
      return;
    }
    if (scrolledToSelection.current === selectedEventId) return;
    const marker = findMarker(containerRef.current, selectedEventId);
    if (!marker) return;
    scrolledToSelection.current = selectedEventId;
    marker.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [selectedEventId, events]);

  const handleScroll = () => {
    const container = containerRef.current;
    const expected = expectedScrollLeft.current;
    if (
      container &&
      expected !== null &&
      Math.abs(container.scrollLeft - expected) < 2
    ) {
      return; // our own scroll
    }
    // The user moved the swimlane: stop following "now" until they ask for
    // the latest event again (the "+N new" chip).
    following.current = false;
  };

  const gridImage = ticks
    .map(
      (tick) =>
        `linear-gradient(to right, transparent calc(${tick.pct}% - 0.5px), rgba(0,0,0,0.08) calc(${tick.pct}% - 0.5px), rgba(0,0,0,0.08) calc(${tick.pct}% + 0.5px), transparent calc(${tick.pct}% + 0.5px))`,
    )
    .join(",");

  const openEvents =
    laneMarkers
      .flatMap(({ markers }) => markers)
      .find((m) => m.key === openCluster?.key)?.events || [];

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      data-testid="timeline-swimlane"
      data-live={live || undefined}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflowX: "auto",
        backgroundColor: "background.paper",
        color: "text.primary",
      }}
    >
      <Box sx={{ minWidth: 760 }}>
        {/* Time axis */}
        <Box
          sx={{
            display: "flex",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ width: LABEL_WIDTH, flexShrink: 0 }} />
          <Box
            ref={trackRef}
            sx={{ position: "relative", flex: 1, height: 28, mx: 2 }}
          >
            {ticks.map((tick) => (
              <Typography
                key={`${tick.pct}-${tick.label}`}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: "absolute",
                  left: `${tick.pct}%`,
                  top: 6,
                  whiteSpace: "nowrap",
                  transform: "translateX(-2px)",
                }}
              >
                {tick.label}
              </Typography>
            ))}
          </Box>
        </Box>

        {laneMarkers.map(({ lane, markers }) => (
          <Box
            key={lane}
            role="group"
            aria-label={tt(`lanes.${lane}`)}
            data-testid={`lane-${lane}`}
            sx={{
              display: "flex",
              borderBottom: "1px solid",
              borderColor: "divider",
              "&:last-of-type": { borderBottom: 0 },
            }}
          >
            <Box
              sx={{
                width: LABEL_WIDTH,
                flexShrink: 0,
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                backgroundColor: "action.hover",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tt(`lanes.${lane}`)}
              </Typography>
            </Box>
            <Box
              sx={{
                position: "relative",
                flex: 1,
                mx: 2,
                height: LANE_HEIGHT,
                backgroundImage: gridImage,
              }}
            >
              {nowPct !== null && (
                <Box
                  aria-hidden
                  data-testid="timeline-now-line"
                  sx={{
                    position: "absolute",
                    left: `${nowPct}%`,
                    top: 0,
                    bottom: 0,
                    width: "2px",
                    backgroundColor: "primary.main",
                    opacity: 0.5,
                  }}
                />
              )}
              {markers.map((marker) => {
                const state = {
                  selected:
                    !!selectedEventId &&
                    marker.events.some((e) => e.id === selectedEventId),
                  focused: marker.events.some((e) =>
                    isEventFocused(e, { geofenceId: focusGeofenceId }),
                  ),
                  isNew: marker.events.some((e) => newEventIds.has(e.id)),
                };
                return marker.events.length === 1 ? (
                  <SingleMarker
                    key={marker.key}
                    event={marker.events[0]}
                    pct={marker.pct}
                    x={marker.x}
                    onSelect={onSelect}
                    {...state}
                  />
                ) : (
                  <ClusterMarker
                    key={marker.key}
                    events={marker.events}
                    pct={marker.pct}
                    x={marker.x}
                    open={openCluster?.key === marker.key}
                    onOpen={(anchor) =>
                      setOpenCluster({ key: marker.key, anchor })
                    }
                    {...state}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Popover
        open={!!openCluster && openEvents.length > 0}
        anchorEl={openCluster?.anchor}
        onClose={() => setOpenCluster(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              // Content surface: never the themed sidebar colours.
              backgroundColor: "background.paper",
              color: "text.primary",
              maxHeight: 320,
              minWidth: 300,
              maxWidth: 420,
            },
          },
        }}
      >
        <MenuList
          autoFocusItem
          dense
          aria-label={t("timeline.clusterListLabel")}
          data-testid="cluster-popover"
        >
          {openEvents.map((event) => {
            const typeMeta = getEventTypeMeta(event);
            return (
              <MenuItem
                key={event.id}
                selected={event.id === selectedEventId}
                data-testid={`cluster-item-${event.id}`}
                onClick={() => {
                  setOpenCluster(null);
                  onSelect(event);
                }}
                sx={{ gap: 1, color: "text.primary", alignItems: "center" }}
              >
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontFamily: "monospace", color: "text.secondary" }}
                >
                  {formatInBackofficeLondon(
                    event.occurredAt,
                    TIMELINE_CLOCK_FORMAT,
                  )}
                </Typography>
                <OutcomeIcon outcome={event.outcome} />
                {typeMeta && (
                  <typeMeta.Icon
                    aria-hidden
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                )}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ whiteSpace: "normal" }}
                >
                  {event.title}
                </Typography>
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </Box>
  );
});

export default TimelineSwimlane;
